import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { PayloadAction } from '@reduxjs/toolkit';

import {
  getPreviewCourseData, getPrevioulsyVisitedCourseList,
  getSearchCourseList, setSelectedCourse,
} from 'redux/actions/ai-course-creation';
import {
  resetDestinationLecturePage, setDestinationLecturePage,
  setLectureAppStateFromRouteParams, unsetcurrentLectureAppState,
} from 'redux/actions/lecture-pages';
import { CourseAliases } from 'redux/schemas/models/course';
import {
  PreviewCourseResponse, SearchCourseList, SearchCourseResult,
} from 'redux/schemas/api/ai-course-creation';
import { getCourseAliases, getCurrentCourse } from 'redux/selectors/course';

import { black, gray2, gray3, primary, white } from 'styles/global_defaults/colors';
import { largeSpacing } from 'styles/global_defaults/scaffolding';

import { Button } from 'react-bootstrap';
import NvPopover from 'shared/components/nv-popover';
import { NvSearchBar } from 'shared/components/nv-search-bar';
import { LecturePageMode } from 'lecture_pages/components';
import LecturePagePreviewModal, {
  LecturePagePreviewContext, PreviewSource,
} from 'lecture_pages/components/lecture-page-preview-modal';
import {
  CourseItemCard, CourseLengthOptions,
  SearchWarningMessage, SetVisualStylingContent,
} from './utils';
import { config } from '@config/config.json';

const pageSize = 5;

interface NovoAISearchProps {
  handleGenerate: (selectedLength: string) => void;
  isGenerateDisabled: boolean;
  generateButtonTooltipText: string;
}

const NovoAISearch = ({ handleGenerate, isGenerateDisabled, generateButtonTooltipText }: NovoAISearchProps) => {
  const [previousVisitedCourses, setPreviousVisitedCourses] = useState<SearchCourseList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchCourses, setSearchCourses] = useState<SearchCourseResult>({
    courses: [],
    pageCount: 0,
    perPage: 0,
    resultsCount: 0,
  });
  const [isSearchInvalid, setIsSearchInvalid] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [currentSelectedCourse, setCurrentSelectedCourse] = useState({});
  const [previewCourseTitle, setPreviewCourseTitle] = useState(null);
  const [isFetchMoreLoading, setIsFetchMoreLoading] = useState(false);

  const currentCourse = useSelector(getCurrentCourse);
  const aliases: CourseAliases = useSelector((state) => getCourseAliases(state));
  const { selectedCourseId } = useSelector((state) => state.app.aiCourseCreation);
  const { id: previewLessonId, catalogId: previewCatalogId } = useSelector(state => state.app.destinationLecturePage);
  const catalogId = useSelector((state) => state.app.currentCatalogId);

  const dispatch = useAppDispatch();
  const inputRef = useRef();

  const styles = css`
    .nv-search-bar {
      input {
       width: 100%;
       color: ${black};
       font-weight: 400;

        &::placeholder {
         font-weight: 300 !important;
         color: ${gray2};
        }
      }
    }

    .icon-search {
      /* To override the default gray2 */
      color: ${black} !important;
    }

    .selected-view-icon {
      opacity: 0;
    }
    .clear-button {
      .text-small{
        color: ${primary} !important;
      }

    }

  .search-box {
    height: ${largeSpacing}px;
    border-bottom: 1px solid ${gray3};
  }
  .show-more-container {
    gap: 2px;
    display: flex;
    align-items: center;

    .show-more-text{
      background : ${white};
      border: none;

      &:hover {
       background-color: ${white} !important;
      }
    }

    .bs4-btn-primary {
      font-weight: 400;
    }
  }
`;

  useEffect(() => {
    getPreviousCourses();
  }, [currentCourse.catalogId]);

  useEffect(() => {
    if (searchValue === '') {
      setIsSearchInvalid(false);
      setIsSearchActive(false);
    }
    if (searchValue.length > 2) {
      setIsSearchInvalid(false);
    }
  }, [searchValue]);

  useEffect(() => {
    if (previewLessonId) {
      dispatch(setLectureAppStateFromRouteParams({
        lecturePageId: previewLessonId,
        catalogId,
        isAdminMode: false,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewLessonId]);

  const getPreviousCourses = () => {
    setIsLoading(true);
    dispatch(getPrevioulsyVisitedCourseList({
      catalogId: currentCourse.catalogId,
    })).then((response) => {
      setPreviousVisitedCourses(response.payload.courses);
      setIsLoading(false);
    });
  };

  const handleSearch = (value) => {
    if (value?.length < 3) {
      setIsSearchInvalid(true);
      return;
    }
    setPageNumber(1);
    dispatch(getSearchCourseList({
      courseCatalogId: currentCourse.catalogId,
      searchParam: searchValue,
      page: 1,
      pageSize,
    })).then((response) => {
      setIsSearchActive(true);
      setSearchCourses(response.payload);
    });
  };

  const loadMoreCourses = () => {
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    setIsFetchMoreLoading(true);
    dispatch(getSearchCourseList({
      courseCatalogId: currentCourse.catalogId,
      searchParam: searchValue,
      page: nextPage,
      pageSize,
    })).then((response) => {
      setIsSearchActive(true);
      setIsFetchMoreLoading(false);
      setSearchCourses((prevData) => ({
        ...prevData,
        courses: [...prevData.courses, ...response.payload.courses],
        pageCount: response.payload.pageCount,
        perPage: response.payload.perPage,
        resultsCount: response.payload.resultsCount,
      }));
    });
  };

  const handleSearchOnchange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleClear = () => {
    setSearchValue('');
    setIsSearchInvalid(false);
  };

  const handlePreviewCouse = (course) => {
    wrapThunkAction(dispatch(getPreviewCourseData({
      courseCatalogId: course.catalogId,
      courseId: course.id,
    }))).then((response: PayloadAction<PreviewCourseResponse>) => {
      dispatch(setDestinationLecturePage({
        mode: LecturePageMode.VIEW,
        lecturePageId: response.payload.allLecturePagesIdsInOrder?.[0],
        catalogId: course.catalogId,
        gallaryIds: response.payload.allLecturePagesIdsInOrder,
      }));

      setPreviewCourseTitle(response.payload.name);
    });
  };

  const onClosePreviewModal = () => {
    setPreviewCourseTitle(null);
    dispatch(unsetcurrentLectureAppState());
    dispatch(resetDestinationLecturePage());
  };

  const showSearchBar = () => (
    <div className='previous-course-search' css={styles}>
      <SetVisualStylingContent />
      <div className='search-box my-4 '>
        <NvPopover
          show={isSearchInvalid}
          content={<SearchWarningMessage />}
          placement='bottom'
        >
          <NvSearchBar
            maxLength={255}
            ref={inputRef}
            aria-label={t.LECTURE_PAGES.LHS.NOVO_AI.CONTENT_SEARCH.SEARCH_CONTENT_INPUT_PLACEHOLDER(aliases.courseAliases)}
            onSearch={(val) => handleSearch(val)}
            value={searchValue}
            onChange={(e) => handleSearchOnchange(e)}
            title={t.LECTURE_PAGES.LHS.NOVO_AI.CONTENT_SEARCH.SEARCH_CONTENT_INPUT_PLACEHOLDER(aliases.courseAliases)}
            placeholder={t.LECTURE_PAGES.LHS.NOVO_AI.CONTENT_SEARCH.SEARCH_CONTENT_INPUT_PLACEHOLDER(aliases.courseAliases)}
            clearButtonEnabled={searchValue !== ''}
            onClearButton={() => handleClear()}
            searchButtonEnabled
            onSearchButtonClick={(val) => handleSearch(val)}
          />
        </NvPopover>
      </div>
    </div>
  );

  const handleSelectedCourse = (course) => {
    if (selectedCourseId === course.id) {
      // Deselect
      dispatch(setSelectedCourse(null));
      setCurrentSelectedCourse({});
    } else {
      // Select
      dispatch(setSelectedCourse(course.id));
      setCurrentSelectedCourse(course);
    }
  };

  const getCourseCount = (course) => {
    if (course.resultsCount - course.courses.length > 5) {
      return 5;
    }
    return course.resultsCount - course.courses.length;
  };

  const isSelectedCourseId = selectedCourseId !== null;

  return (
    <div className='course-listing-main mt-8' css={styles}>
      <div className='course-search'>
        {!isLoading && previousVisitedCourses.length === 0 && !isSelectedCourseId && <CourseLengthOptions handleGenerate={handleGenerate} isGenerateDisabled={isGenerateDisabled} generateButtonTooltipText={generateButtonTooltipText} />}
        {previousVisitedCourses.length !== 0 && showSearchBar()}
        {previousVisitedCourses.length !== 0 && !isSearchActive && !isSelectedCourseId && (
          <React.Fragment>
            <div className='suggestions-txt text-gray-2 label pb-1' data-qa={config.pendo.novoAiCourseCreation.previouslyVisited}>
              {t.LECTURE_PAGES.LHS.NOVO_AI.PREVIOUSLY_VISITED()}
            </div>
            {previousVisitedCourses?.map((course) => (
              <CourseItemCard
                key={course.id}
                course={course}
                handlePreviewCouse={() => handlePreviewCouse(course)}
                handleCourseItem={() => handleSelectedCourse(course)}
              />
            ))}
          </React.Fragment>
        )}
        {isSearchActive && searchCourses.courses.length !== 0 && !isSelectedCourseId && (
          <React.Fragment>
            <div className='suggestions-txt text-gray-2 label pb-1' data-qa={config.pendo.novoAiCourseCreation.search.searchResultCount}>
              {t.SEARCH.SEARCH_RESULTS_NUM(searchCourses.resultsCount)}
            </div>
            {searchCourses?.courses?.map((course) => (
              <CourseItemCard
                key={course.id}
                course={course}
                handlePreviewCouse={() => handlePreviewCouse(course)}
                handleCourseItem={() => handleSelectedCourse(course)}
              />
            ))}
            {getCourseCount(searchCourses) ? (
              <div className='show-more-container text-small p-0 mb-8'>
                <Button
                  className='show-more-text text-small p-0'
                  onClick={loadMoreCourses}
                  disabled={isFetchMoreLoading}
                  data-qa={config.pendo.novoAiCourseCreation.search.showMoreCourses}
                >
                  <span className='text-primary'>{t.LECTURE_PAGES.LHS.NOVO_AI.CONTENT_SEARCH.SHOW_MORE_COURSES(getCourseCount(searchCourses))}</span>
                </Button>
                <span className='text-gray-2'>{t.LECTURE_PAGES.LHS.NOVO_AI.CONTENT_SEARCH.REFINE_SEARCH_RESULTS()}</span>
              </div>
            )
              : <div className='text-gray-2 text-small mb-8' data-qa={config.pendo.novoAiCourseCreation.search.endOfResults}>{t.LHS.CONTENT_SEARCH.END_OF_RESULTS()}</div>}
          </React.Fragment>
        )}
        {isSearchActive && searchCourses.resultsCount === 0 && <div className='text-gray-2 label'> {t.LECTURE_PAGES.LHS.NOVO_AI.CONTENT_SEARCH.NO_SEARCH_RESULT()} </div>}
        {isSelectedCourseId
          && (
            <React.Fragment>
              <div className='suggestions-txt text-gray-2 label pb-1' data-qa={config.pendo.novoAiCourseCreation.selected}>
                {t.LECTURE_PAGES.LHS.NOVO_AI.SELECTED()}
              </div>
              <CourseItemCard
                course={currentSelectedCourse}
                handlePreviewCouse={() => handlePreviewCouse(currentSelectedCourse)}
                handleCourseItem={() => handleSelectedCourse(currentSelectedCourse)}
              />
              <CourseLengthOptions handleGenerate={handleGenerate} isGenerateDisabled={isGenerateDisabled} generateButtonTooltipText={generateButtonTooltipText} />
            </React.Fragment>
          )}
      </div>
      {previewLessonId && previewCatalogId && (
        <LecturePagePreviewContext.Provider value={{
          lecturePageId: previewLessonId,
          source: PreviewSource.NORMAL,
          onClosePreviewModal,
        }}
        >
          <LecturePagePreviewModal
            // Using the title state to determine if the modal should be shown
            show={!!previewCourseTitle}
            onClose={onClosePreviewModal}
            header={t.LECTURE_PAGES.LHS.NOVO_AI.COURSE_PREVIEW_MODAL_HEADER(previewCourseTitle)}
          />
        </LecturePagePreviewContext.Provider>
      )}
    </div>
  );
};

export default NovoAISearch;
